import axiosInstance from '@/lib/axios';
import { EmployeeFormValues } from '@/components/employees/AddEmployeeDialog';
import { baseURL } from '@/lib/axios';
import { getSecureItem } from '@/utils/localStorage';

// Types
export interface Employee {
  id: string;
  _id?: string;
  employeeId?: string;
  profilePicture?: string;
  user?: {
    _id?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    roles?: string[];
  };
  department?: { _id?: string; id?: string; name?: string } | string;
  role?: { name?: string } | string;
  status?: string;
  joinDate?: Date | string;
  joiningDate?: Date | string;
  phone?: string;
  salary?: number;
  jobInfo?: {
    joiningDate?: Date;
    location?: string;
    jobTitle?: string;
    category?: string;
    reportingManager?: string[] | { _id: string; firstName: string; lastName: string; employeeId: string }[];
    lineManager?: string[] | { _id: string; firstName: string; lastName: string; employeeId: string }[];
    lineManager2?: string[] | { _id: string; firstName: string; lastName: string; employeeId: string }[];
    employmentStatus?: string;
    probationPeriod?: number;
    probationEndDate?: Date;
    feedbackRemainderDays?: number;
    notifyDay?: number;
    note?: string;
  };
  // Virtual populate fields from backend
  reportingManagers?: { _id: string; firstName: string; lastName: string; employeeId: string; email: string }[];
  lineManagers?: { _id: string; firstName: string; lastName: string; employeeId: string; email: string }[];
  lineManagers2?: { _id: string; firstName: string; lastName: string; employeeId: string; email: string }[];
  agreementInfo?: {
    startDate?: Date;
    endDate?: Date;
    periodInMonths?: string;
    notifyPeriodInDays?: string;
    note?: string;
  };
  compensationInfo?: {
    startDate?: Date;
    endDate?: Date;
    newSalary?: string;
    payFrequency?: string;
    noticePeriodInDays?: string;
    notifyPeriodInDays?: string;
    salaryMode?: string;
    note?: string;
  };

  createdAt?: string;
  [key: string]: any;
}

// Document-related interfaces
export interface Document {
  id: string;
  name: string;
  number: string;
  fullName: string;
  attachments: string[];
  expiredOn: string;
  status: 'active' | 'expired' | 'pending' | 'draft';
  type: 'personal' | 'official';
  description?: string;
  uploadedOn: string;
  lastModified: string;
}

export interface DocumentData {
  personalDocuments?: Document[];
  officialDocuments?: Document[];
}

export interface EmployeeStats {
  totalEmployees: number;
  activeEmployees: number;
  departments: number;
  onLeaveEmployees: number;
  newEmployeesThisMonth: number;
  departmentDistribution: { [key: string]: number };
}

export interface EmployeeFilters {
  search?: string;
  department?: string;
  role?: string;
  status?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

// Constants
export const DEPARTMENTS = ["Engineering", "Design", "Sales", "HR", "Marketing", "Finance", "Operations"];
export const ROLES = ["Software Engineer", "UI/UX Designer", "Product Manager", "Sales Executive", "HR Manager", "Marketing Specialist", "Team Lead", "Project Manager", "Business Analyst", "DevOps Engineer", "QA Engineer"];
export const STATUS_OPTIONS = ["Full-time", "Part-time", "Probation", "Contract", "Work from Home", "Intern", "Terminated"];

// Get dropdown options for employee forms
export async function getEmployeeDropdownOptions() {
  const response = await axiosInstance.get('/employees/dropdown-options');
  return response.data;
}

// Get all employees
export async function getEmployees() {
  const response = await axiosInstance.get('/employees');
  return response.data;
}

// Get all employees for manager dropdowns
export async function getEmployeesForDropdown() {
  const response = await axiosInstance.get('/employees');
  const data = response.data;
  
  // Handle different response structures
  const employees = Array.isArray(data) ? data : (data.data || data.employees || []);
  
  // Transform employees to dropdown format
  return employees.map((employee: any) => ({
    _id: employee.user?._id || employee._id || employee.id,
    id: employee.user?._id || employee._id || employee.id,
    name: `${employee.user?.firstName || employee.firstName || ''} ${employee.user?.lastName || employee.lastName || ''}`.trim(),
    employeeId: employee.employeeId || employee.user?.employeeId,
    email: employee.user?.email || employee.email
  })).filter((emp: any) => emp.name && emp.name.trim() !== '');
}

// Get employees for mentions (when typing @)
export interface MentionUser {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  employeeId: string;
  profilePicture?: string | null;
}

export async function getEmployeesForMentions(search?: string): Promise<MentionUser[]> {
  try {
    const params = search ? { search } : {};
    const response = await axiosInstance.get<MentionUser[]>('/employees/mentions', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching employees for mentions:', error);
    throw error;
  }
}

// Get employee by ID
export async function getEmployeeById(id: string | number) {
  const response = await axiosInstance.get(`/employees/${id}`);
  return response.data;
}

// Create a new employee
export async function createEmployee(data: EmployeeFormValues) {
  const response = await axiosInstance.post('/employees', data);
  return response.data;
}

// Update employee
export async function updateEmployee(id: string | number, data: any) {
  // Create FormData object as expected by backend
  const formData = new FormData();
  
  // Clean up the data object - remove empty fields
  const cleanData: any = {};
  Object.keys(data).forEach((key) => {
    if (data[key] !== undefined && data[key] !== null && data[key] !== "") {
      if (typeof data[key] === "object" && !Array.isArray(data[key])) {
        // For nested objects, only include if they have at least one non-empty value
        const nestedObj = data[key];
        const hasValues = Object.values(nestedObj).some(
          (val) => val !== undefined && val !== null && val !== ""
        );
        if (hasValues) {
          cleanData[key] = nestedObj;
        }
      } else if (Array.isArray(data[key])) {
        // For arrays, only include if they have at least one item
        if (data[key].length > 0) {
          cleanData[key] = data[key];
        }
      } else {
        cleanData[key] = data[key];
      }
    }
  });
  
  // Add the data as a JSON string - this is what the backend expects
  formData.append("data", JSON.stringify(cleanData));
  
  const response = await axiosInstance.patch(`/employees/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}

// Delete employee
export async function deleteEmployee(id: string | number) {
  const response = await axiosInstance.delete(`/employees/${id}`);
  return response.data;
}

// Get all departments
export async function getDepartments() {
  const response = await axiosInstance.get('/departments');
  return response.data;
}

// Get all roles
export async function getRoles() {
  const response = await axiosInstance.get('/roles');
  return response.data;
}

// Get employee by employeeId (string, not DB id)
export async function getEmployeeByEmployeeId(employeeId: string) {
  const response = await axiosInstance.get(`/employees/by-employee-id/${employeeId}`);
  return response.data;
}

// Get current user's employee profile
export async function getCurrentEmployeeProfile() {
  const response = await axiosInstance.get('/employees/me');
  return response.data;
}

// Assign a role to a user (new API)
export async function assignRoleToUser(userId: string, roleId: string) {
  await axiosInstance.put(`/employees/${userId}/roles`, {
    roles: [roleId],
  });
}

// Unassign roles from a user (new API)
export async function unassignRoleFromUser(userId: string) {
  
  // Based on backend error messages, the API doesn't support unassignment
  // Instead, we'll assign a default "Employee" role to effectively unassign the current role
  try {
    
    // First, try to get the default Employee role ID
    const rolesResponse = await axiosInstance.get('/roles');
    const roles = rolesResponse.data;
    
    // Handle different response structures
    const rolesArray = Array.isArray(roles) ? roles : (roles.data || roles.roles || []);
    
    const employeeRole = rolesArray.find((role: any) => 
      role.name === 'Employee' || 
      role.name === 'employee' || 
      role.name === 'EMPLOYEE'
    );
    
    
    if (employeeRole) {
      // Assign the Employee role instead of unassigning
      const roleIdToAssign = employeeRole.id || employeeRole._id;
      
      try {
        await axiosInstance.put(`/employees/${userId}/roles`, {
          roles: [roleIdToAssign],
        });
        return;
      } catch (assignError: any) {
        
        // Try assigning by name instead of ID
        await axiosInstance.put(`/employees/${userId}/roles`, {
          roles: [employeeRole.name],
        });
        return;
      }
    } else {
      // If no Employee role exists, try to assign an empty role or the first available role
      if (rolesArray.length > 0) {
        const firstRole = rolesArray[0];
        const firstRoleId = firstRole.id || firstRole._id;
        
        try {
          await axiosInstance.put(`/employees/${userId}/roles`, {
            roles: [firstRoleId],
          });
            return;
        } catch (assignError: any) {
          
          // Try assigning by name instead of ID
          await axiosInstance.put(`/employees/${userId}/roles`, {
            roles: [firstRole.name],
          });
          return;
        }
      } else {
        throw new Error('No roles available in the system');
      }
    }
  } catch (error: any) {
    
    // If all else fails, try to assign an empty string role (some backends accept this)
    try {
      await axiosInstance.put(`/employees/${userId}/roles`, {
        roles: [''],
      });
      return;
    } catch (finalError: any) {
      throw new Error('Unable to unassign role. The backend does not support role unassignment. Please assign a different role instead.');
    }
  }
}

// Get current user's profile
export const getMyProfile = async (): Promise<Employee> => {
  try {
    const response = await axiosInstance.get('/employees/me');
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// Update current user's profile (same pattern as onboarding)
export const updateProfile = async (employeeId: string, updateData: Partial<Employee>, files?: File[]): Promise<Employee> => {
  try {
    // Always use FormData - backend expects it (same as onboarding)
    const backendFormData = new FormData();
    
    // Clean up the data object - remove empty fields (same as onboarding)
    const cleanData: any = {};
    Object.keys(updateData).forEach((key) => {
      if (updateData[key] !== undefined && updateData[key] !== null && updateData[key] !== "") {
        if (typeof updateData[key] === "object" && !Array.isArray(updateData[key])) {
          // For nested objects, only include if they have at least one non-empty value
          const nestedObj = updateData[key];
          const hasValues = Object.values(nestedObj).some(
            (val) => val !== undefined && val !== null && val !== ""
          );
          if (hasValues) {
            cleanData[key] = nestedObj;
          }
        } else if (Array.isArray(updateData[key])) {
          // For arrays, include them even if empty (needed for banking deletion)
          // Special case: always include banking array even if empty
          if (key === 'banking' || updateData[key].length > 0) {
            cleanData[key] = updateData[key];
          }
        } else {
          cleanData[key] = updateData[key];
        }
      }
    });
    
    // Add the data as a JSON string - this is what the backend expects (same as onboarding)
    backendFormData.append("data", JSON.stringify(cleanData));
    
    // Add files with the correct field names expected by the backend
    if (files && files.length > 0) {
      files.forEach((file) => {
        // Determine file type based on the data being updated
        if (cleanData.banking) {
          backendFormData.append("bankingAttachments", file);
        } else if (cleanData.documents) {
          backendFormData.append("documentAttachments", file);
        } else {
          // Default to document attachments for general updates
          backendFormData.append("documentAttachments", file);
        }
      });
    }
    
    const response = await axiosInstance.patch(`/employees/${employeeId}`, backendFormData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// Clean up duplicate employee details records
export async function cleanupDuplicateEmployeeDetails() {
  const response = await axiosInstance.post('/employees/cleanup-duplicates');
  return response.data;
}

// Update banking information with file uploads
export const updateBankingInfo = async (employeeId: string, bankingData: any, files?: File[]): Promise<Employee> => {
  try {
    const formData = new FormData();
    
    // Add banking data as JSON string
    formData.append('data', JSON.stringify(bankingData));
    
    // Add files if they exist
    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append('bankingAttachments', file);
      });
    }
    
    const response = await axiosInstance.patch(`/employees/${employeeId}/banking`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Document-related functions
export const updateDocuments = async (employeeId: string, documentData: DocumentData, files?: File[]): Promise<any> => {
  try {
    const token = getSecureItem('accessToken');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const formData = new FormData();
    
    // Send documents data in the flattened format for the backend
    const payload = { documents: documentData };
    formData.append('data', JSON.stringify(payload));

    // Add files to form data with the correct field name for document attachments
    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append('documentAttachments', file);
      });
    }

    const response = await fetch(`${baseURL}/employees/${employeeId}/documents`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update documents');
    }

    const result = await response.json();
    return result;
  } catch (error) {
    throw error;
  }
};

// Helper function to get complete backend URL for documents
export const getCompleteBackendUrl = (docUrl: string): string => {
  if (!docUrl) return '';
  const baseUrl = baseURL.replace('/api/v1', '');
  const hash = getDocumentHash(docUrl);
  return `${baseUrl}/uploads/document-attachments/${hash}`;
};

// Helper function to extract hash from document URL
export const getDocumentHash = (docUrl: string): string => {
  if (!docUrl) return 'document.pdf';
  const parts = docUrl.split('/');
  return parts[parts.length - 1] || 'document.pdf';
};

// Download document file
export const downloadDocument = async (docUrl: string, fileName?: string): Promise<void> => {
  try {
    const backendFileName = getDocumentHash(docUrl);
    const completeBackendUrl = getCompleteBackendUrl(docUrl);
    
    const token = getSecureItem('accessToken');
    
    if (token) {
      try {
        const response = await fetch(completeBackendUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/pdf,application/octet-stream,*/*',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        
        // Create download link
        const downloadLink = document.createElement('a');
        downloadLink.href = blobUrl;
        downloadLink.download = fileName || backendFileName;
        downloadLink.style.display = 'none';
        
        // Trigger download
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        
        // Clean up
        window.URL.revokeObjectURL(blobUrl);
      } catch (fetchError) {
        // Fallback to direct link
        const link = document.createElement('a');
        link.href = completeBackendUrl;
        link.download = fileName || backendFileName;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.click();
      }
    } else {
      // No token, use direct link
      const link = document.createElement('a');
      link.href = completeBackendUrl;
      link.download = fileName || backendFileName;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.click();
    }
  } catch (error) {
    // Final fallback - open in new tab
    const completeBackendUrl = getCompleteBackendUrl(docUrl);
    window.open(completeBackendUrl, '_blank');
  }
};

// Profile picture upload
export const uploadProfilePicture = async (employeeId: string, file: File): Promise<any> => {
  try {
    const formData = new FormData();
    formData.append('profilePicture', file);

    const response = await axiosInstance.patch(
      `/employees/${employeeId}/profile-picture`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error uploading profile picture:', error);
    throw error;
  }
};

// Delete profile picture
export const deleteProfilePicture = async (employeeId: string): Promise<any> => {
  try {
    const response = await axiosInstance.delete(`/employees/${employeeId}/profile-picture`);
    return response.data;
  } catch (error) {
    console.error('Error deleting profile picture:', error);
    throw error;
  }
};

export const employeeService = {
  getMyProfile,
  updateProfile,
  updateBankingInfo,
  cleanupDuplicateEmployeeDetails,
  updateDocuments,
  downloadDocument,
  getCompleteBackendUrl,
  getDocumentHash,
  uploadProfilePicture,
  deleteProfilePicture
};
